import util from '../lib/util';
import * as DateUtil from '../lib/dateutils';
import newHRTimestamp from '../lib/picos/timestamp';
import type {uuid, SchemaField, EntitySchema} from "../@types/schema";
import Base from "./base";
import {genUUID} from "../lib/picos/uuids";
import type {EntityType} from "../@types/entity";
function getCurrentUserId() { return sessionStorage.getItem('userId'); }
function getCurrentFirmId() { return sessionStorage.getItem('firmId'); }
export const Priority = {
    Low: "L",
    Medium: "M",
    High: "H"
};
export type PriorityType = typeof Priority[keyof typeof Priority];
export const Source = {
    Automatic: "A",
    Manual: "M"
};
export type SourceType = typeof Source[keyof typeof Source];
export const AssignedToType = {
    User: "U",
    Group: "G"
};
export type AssignedToTypeType = typeof AssignedToType[keyof typeof AssignedToType];
class Deadline extends Base implements EntityType {
    deadlineId: uuid;
    matterId: uuid;
    deadlineRuleId?: uuid | null;
    eventId?: uuid | null;
    title: string;
    description?: string | null;
    triggerDate?: Date | null;
    dueDate: Date;
    completedOn?: Date | null;
    completedBy?: uuid | null;
    priority: PriorityType;
    color?: string | null;
    reminderDaysBefore?: number | null;
    reminderSent: boolean;
    source?: SourceType | null;
    assignedToType: AssignedToTypeType;
    assignedTo?: uuid | null;
    notes?: string | null;
    constructor(doc: typeof Deadline | any) {
        super(doc);
        if (!doc) doc = {
            firmId: getCurrentFirmId(),
            addedBy: getCurrentUserId(),
            addedOn: newHRTimestamp(),
            status: "C",
            entityId: null,
            entityType: null,
            deadlineId: genUUID(),
            matterId: null,
            title: null,
            dueDate: null,
        };
        // @ts-ignore
        this.entityId = doc._entityId || doc.entityId || doc.entityid || '00000000-0000-0000-0000-000000000000';
        // @ts-ignore
        this.entityType = doc._entityType || doc.entityType || doc.entitytype || "deadline";
        // @ts-ignore
        this.deadlineId = doc._deadlineId || doc.deadlineId || doc.deadlineid || genUUID();
        // @ts-ignore
        this.matterId = doc._matterId || doc.matterId || doc.matterid || null;
        // @ts-ignore
        this.deadlineRuleId = doc._deadlineRuleId || doc.deadlineRuleId || doc.deadlineruleid || null;
        // @ts-ignore
        this.eventId = doc._eventId || doc.eventId || doc.eventid || null;
        // @ts-ignore
        this.title = doc._title || doc.title || null;
        // @ts-ignore
        this.description = doc._description || doc.description || null;
        // @ts-ignore
        this.triggerDate = DateUtil.timestampOnly(doc._triggerDate) || DateUtil.timestampOnly(doc.triggerDate) || DateUtil.timestampOnly(doc.triggerdate) || null;
        // @ts-ignore
        this.dueDate = DateUtil.timestampOnly(doc._dueDate) || DateUtil.timestampOnly(doc.dueDate) || DateUtil.timestampOnly(doc.duedate) || null;
        // @ts-ignore
        this.completedOn = DateUtil.timestampOnly(doc._completedOn) || DateUtil.timestampOnly(doc.completedOn) || DateUtil.timestampOnly(doc.completedon) || null;
        // @ts-ignore
        this.completedBy = doc._completedBy || doc.completedBy || doc.completedby || null;
        // @ts-ignore
        this.priority = util.trimRight(doc._priority) || util.trimRight(doc.priority) || "M";
        // @ts-ignore
        this.color = doc._color || doc.color || null;
        // @ts-ignore
        this.reminderDaysBefore = util.coalesce(util.parseInt(doc._reminderDaysBefore), util.parseInt(doc.reminderDaysBefore), util.parseInt(doc.reminderdaysbefore), null);
        // @ts-ignore
        this.reminderSent = util.defaultBoolean([doc.reminderSent, doc._reminderSent, doc.remindersent, false]);
        // @ts-ignore
        this.source = util.trimRight(doc._source) || util.trimRight(doc.source) || null;
        // @ts-ignore
        this.assignedToType = util.trimRight(doc._assignedToType) || util.trimRight(doc.assignedToType) || util.trimRight(doc.assignedtotype) || "U";
        // @ts-ignore
        this.assignedTo = doc._assignedTo || doc.assignedTo || doc.assignedto || null;
        // @ts-ignore
        this.notes = doc._notes || doc.notes || null;
    }
    get id(): uuid { return this.deadlineId; }
    static PRIORITY_VALUES = ["L", "M", "H"];
    static SOURCE_VALUES = ["A", "M"];
    static ASSIGNED_TO_TYPE_VALUES = ["U", "G"];
    static override getPkFields(): SchemaField[] { return [{"type":"uuid","required":true,"pk":true,"default":"__UUID__","comment":"Unique id for this deadline"},{"type":"uuid","required":true,"pk":true,"base":true,"default":"__FIRM__","mergeStrategy":"immutable","comment":"Associated firm"}]; }
    toLowerCase() { return this.deadlineId; }
    static override getNonRefArrayFields(): string[] { return []; }
    static override getDefaultEntries(): Deadline[] {
        return [];
    }
    static override getSchema(): EntitySchema {
        return Base.withBaseFields({
    entity: 'deadline',
    dataArea: 'Cases',
    attachedTo: {parents: ['matter'], via: 'matterId', onDelete: 'delete', onReplace: 'move'},
    table: 'DEADLINE',
    comment: 'A deadline is a due-date milestone on a specific case, tracking what needs to be done and when. Deadlines are created automatically from DeadlineRule templates (triggered by case creation or stage changes) or added manually by users. Each deadline records a due date, completion status, priority, assignee, and optional reminder, and appears on the case card and task calendar.',
    fields: {
        deadlineId: {
            type: 'uuid',
            required: true,
            pk: true,
            default: '__UUID__',
            comment: 'Unique id for this deadline'
        },

        /**
         * The case this deadline belongs to
         */
        matterId: {
            type: 'uuid',
            required: true,
            entityType: 'matter',
            comment: 'The case this deadline belongs to'
        },

        /**
         * The rule that generated this deadline (null if manually created)
         */
        deadlineRuleId: {
            type: 'uuid',
            entityType: 'deadlineRule',
            comment: 'The rule that generated this deadline (null if manual)'
        },

        /**
         * The event that triggered this deadline (null if not event-based)
         */
        eventId: {
            type: 'uuid',
            entityType: 'event',
            comment: 'The event that triggered this deadline (null if not event-based)'
        },

        /**
         * Display title — snapshotted from rule or user-entered
         */
        title: {
            type: 'string',
            required: true,
            searchable: true,
            comment: 'Deadline title'
        },

        /**
         * Detailed description
         */
        description: {
            type: 'string',
            comment: 'Deadline description'
        },

        /**
         * The date that triggered the calculation (for audit trail)
         */
        triggerDate: {
            type: 'timestamp',
            comment: 'The date that triggered deadline calculation'
        },

        /**
         * The calculated (or manually set) due date
         */
        dueDate: {
            type: 'timestamp',
            required: true,
            comment: 'When this deadline is due'
        },

        /**
         * When this deadline was marked complete
         */
        completedOn: {
            type: 'timestamp',
            comment: 'When this deadline was completed'
        },

        /**
         * Who marked this deadline complete
         */
        completedBy: {
            type: 'uuid',
            entityType: 'user',
            comment: 'Who completed this deadline'
        },

        /**
         * Priority level
         */
        priority: {
            type: 'char',
            values: ['L', 'M', 'H'],
            valueNames: ['Low', 'Medium', 'High'],
            default: 'M',
            comment: 'Priority: [L]ow, [M]edium, [H]igh'
        },

        /**
         * Hex color for visual display
         */
        color: {
            type: 'string',
            comment: 'Hex color code for visual display'
        },

        /**
         * Days before due date to send a reminder
         */
        reminderDaysBefore: {
            type: 'int',
            comment: 'Days before due date to send reminder'
        },

        /**
         * Whether the reminder has been sent
         */
        reminderSent: {
            type: 'boolean',
            comment: 'Has the reminder been sent?'
        },

        /**
         * How this deadline was created:
         * [A]utomatic - from a rule trigger
         * [M]anual - user-created
         */
        source: {
            type: 'char',
            values: ['A', 'M'],
            valueNames: ['Automatic', 'Manual'],
            comment: 'Source: [A]utomatic from rule, [M]anual'
        },

        /**
         * Are we assigning this to a user or a group?
         */
        assignedToType: {
            type: 'char',
            values: ['U', 'G'],
            valueNames: ['User', 'Group'],
            default: 'U',
            entity: {
                map: {U: 'user', G: 'group'},
                value: 'assignedTo',
                deleteStrategy: 'preserve'
            },
            comment: 'Are we assigning this deadline to a user or a group?'
        },

        /**
         * User or group responsible for this deadline
         */
        assignedTo: {
            type: 'uuid',
            comment: 'User or group assigned to this deadline'
        },

        /**
         * Additional notes
         */
        notes: {
            type: 'string',
            comment: 'Additional notes'
        }
    },
    indexes: [
        {name: 'notDeleted', fn: s => s.status !== 'D'},
        {name: 'pending', fn: s => !s.completedOn && s.status !== 'D'},
        {name: 'completed', fn: s => !!s.completedOn && s.status !== 'D'}
    ],
    maps: [
        {
            name: 'matterId',
            keysFn: s => s.matterId,
            presentFn: (s, k) => s.matterId === k && s.status !== 'D'
        },
        {
            name: 'deadlineRuleId',
            keysFn: s => s.deadlineRuleId,
            presentFn: (s, k) => s.deadlineRuleId === k && s.status !== 'D'
        },
        {
            name: 'eventId',
            keysFn: s => s.eventId,
            presentFn: (s, k) => s.eventId === k && s.status !== 'D'
        },
        {
            name: 'userId',
            keysFn: s => s.completedBy,
            presentFn: (s, k) => s.completedBy === k && s.status !== 'D'
        },
        {
            name: 'calendarDate',
            keysFn: s => s.dueDate ? new Date(s.dueDate).toISOString().split('T')[0] : null,
            presentFn: (s, k) => s.dueDate && new Date(s.dueDate).toISOString().split('T')[0] === k && s.status !== 'D'
        }
    ]
});
    }
}
export default Deadline;
